import React, { useState } from 'react';
import { Form, DatePicker, TimePicker, Select, Button, Alert, Row, Col, Typography, Modal, message } from 'antd';
import dayjs from 'dayjs';
import { ProjectTwoTone, StarTwoTone, WarningTwoTone, BulbTwoTone, FireTwoTone, RocketTwoTone, SafetyCertificateTwoTone, MoneyCollectTwoTone, HeartTwoTone, TeamOutlined, BankTwoTone, CompassTwoTone, FieldTimeOutlined, PushpinTwoTone, ThunderboltTwoTone, EyeTwoTone, FileTextTwoTone, CheckCircleTwoTone, CloseCircleTwoTone, UserOutlined } from '@ant-design/icons';
import { BaziChartGrid } from '../../components/BaziChartGrid';
import tenGodsProfiles from '../../data/ten_gods_profiles.json';

const { Title, Text } = Typography;

interface Pillar {
  position: string;
  stem: string;
  branch: string;
  element?: string;
  pinyin?: string;
  ciongStem?: boolean;
  ciongBranch?: boolean;
}

interface LuckCycle {
  stem: string;
  branch: string;
  pinyin: string;
  element: string;
  elementCh: string;
  start_age: number;
  end_age: number;
  start_year: number;
  end_year: number;
}

interface ChartData {
  chart_id?: string;
  date_of_birth?: string;
  time_of_birth?: string;
  gender?: string;
  pillars?: Pillar[];
  day_master_strength?: string;
  day_master_scores?: { total_score?: number; hour_stem_score?: number; day_stem_score?: number; month_stem_score?: number; year_stem_score?: number };
  pilar_bumi_scores?: { total_score?: number; hour_branch_score?: number; day_branch_score?: number; month_branch_score?: number; year_branch_score?: number };
  unknown_tob?: boolean;
  bazi_compatibility_score?: number;
  bazi_compatibility_label?: string;
  ten_relations?: {
    year_stem?: string;
    month_stem?: string;
    hour_stem?: string;
  };
  luck_cycles?: LuckCycle[];
  kua?: {
    kua_number: number;
    trigram: string;
    group: string;
    directions?: {
      favorable?: Record<string, { direction: string; name: string; meaning: string }>;
      unfavorable?: Record<string, { direction: string; name: string; meaning: string }>;
    };
  };
  career_health?: {
    element?: { name?: string };
    info?: { karakteristik?: string; kinerja?: string };
    details?: { element_pendukung?: string[]; karir?: string[]; penyakit?: string[] };
  };
  strategy_guide?: {
    keuangan: string;
    kesehatan: string;
    keluarga: string;
    partner: string;
    pekerjaan: string;
    bisnis: string;
    narasi?: string;
  };
  internal_clashes?: Array<{ name: string; impact: string }>;
  elements?: Record<string, number>;

}

interface AdminFreeFormProps {
  token: string;
  API_URL: string;
}

export const AdminFreeForm: React.FC<AdminFreeFormProps> = ({ token, API_URL }) => {
  const [loading, setLoading] = useState(false);
  const [chart, setChart] = useState<ChartData | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [form] = Form.useForm();
  const [unknownTob, setUnknownTob] = useState(false);
  const [isChartVisible, setIsChartVisible] = useState(false);

  // Transit / custom date states
  const [useTransit, setUseTransit] = useState(false);
  const [transitLoading, setTransitLoading] = useState(false);
  const [transitResult, setTransitResult] = useState<any>(null);
  const [transitChartData, setTransitChartData] = useState<ChartData | null>(null);
  const [transitForm] = Form.useForm();
  const [transitUnknownTob, setTransitUnknownTob] = useState(false);
  const [isTransitChartVisible, setIsTransitChartVisible] = useState(false);

  const handleCalculate = async (values: any) => {
    setLoading(true);
    setErrorMsg('');
    setChart(null);
    setTransitResult(null);
    setTransitChartData(null);

    try {
      if (!values || !values.dob) {
        throw new Error('Silakan pilih tanggal lahir.');
      }

      const dobStr = typeof values.dob.format === 'function' ? values.dob.format('YYYY-MM-DD') : String(values.dob);
      const tobStr = unknownTob ? '00:00' : (values.tob ? (typeof values.tob.format === 'function' ? values.tob.format('HH:mm') : String(values.tob)) : '12:00');

      const res = await fetch(`${API_URL}/api/v1/personal/charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date_of_birth: dobStr,
          time_of_birth: tobStr,
          timezone: values.timezone || 'Asia/Jakarta',
          gender: values.gender || 'male',
          person_name: values.name || 'Admin Kalkulasi',
          unknown_tob: unknownTob
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Gagal menghitung BaZi');
      }

      const data = await res.json();
      const chartPayload = data.data || data;
      setChart(chartPayload);
      message.success('Kalkulasi BaZi berhasil!');

      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err: any) {
      setErrorMsg(err.message || 'Koneksi ke server gagal.');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitCalculate = async (values: any) => {
    setTransitLoading(true);
    setTransitResult(null);
    setTransitChartData(null);

    try {
      if (!values || !values.transit_dob) {
        throw new Error('Silakan pilih tanggal pembanding.');
      }
      if (!chart?.chart_id) {
        throw new Error('Hitung bagan utama terlebih dahulu.');
      }

      const transitDobStr = typeof values.transit_dob.format === 'function' ? values.transit_dob.format('YYYY-MM-DD') : String(values.transit_dob);
      const transitTobStr = transitUnknownTob ? '00:00' : (values.transit_tob ? (typeof values.transit_tob.format === 'function' ? values.transit_tob.format('HH:mm') : String(values.transit_tob)) : '12:00');

      // Hitung ulang dengan transit date (existing API — no date restrictions for admin)
      const res = await fetch(`${API_URL}/api/v1/personal/charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date_of_birth: chart.date_of_birth,
          time_of_birth: chart.time_of_birth || '12:00',
          timezone: values.timezone || 'Asia/Jakarta',
          gender: chart.gender || 'male',
          person_name: 'Admin Kalkulasi',
          unknown_tob: chart.unknown_tob || false,
          transit_date: transitDobStr,
          transit_time: transitTobStr
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Gagal menghitung keselarasan tanggal');
      }

      const data = await res.json();
      const chartPayload = data.data || data;

      setTransitResult({
        bazi_synergy_score: chartPayload.bazi_compatibility_score,
        bazi_synergy_label: chartPayload.bazi_compatibility_label,
        strategy_guide: chartPayload.strategy_guide
      });

      // Fetch custom date's own chart for "Lihat Bagan Pembanding"
      try {
        const customChartRes = await fetch(`${API_URL}/api/v1/personal/charts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            date_of_birth: transitDobStr,
            time_of_birth: transitTobStr,
            timezone: values.timezone || 'Asia/Jakarta',
            gender: chart.gender || 'male',
            person_name: 'Tanggal Custom',
            unknown_tob: transitUnknownTob
          })
        });
        if (customChartRes.ok) {
          const customChartData = await customChartRes.json();
          const customChartPayload = customChartData.data || customChartData;
          setTransitChartData({ ...customChartPayload, unknown_tob: transitUnknownTob });
        } else {
          setTransitChartData({ ...chartPayload, unknown_tob: transitUnknownTob });
        }
      } catch (_e) {
        setTransitChartData({ ...chartPayload, unknown_tob: transitUnknownTob });
      }

      message.success('Keselarasan tanggal berhasil dihitung!');
    } catch (err: any) {
      message.error(err.message || 'Gagal menghitung keselarasan.');
    } finally {
      setTransitLoading(false);
    }
  };

  const getScoreBadgeStyle = (label: string) => {
    const lbl = (label || 'AVERAGE').toUpperCase();
    let bg = '#FFFF65', color = '#555000';
    if (lbl === 'VERY GOOD') { bg = '#e8f4fd'; color = '#0071C1'; }
    if (lbl === 'GOOD') { bg = '#B5E6A2'; color = '#2d6b1e'; }
    if (lbl === 'CHALLENGE') { bg = '#FF66FF'; color = '#990099'; }
    if (lbl === 'BAD') { bg = '#ffe6ee'; color = '#FF0068'; }
    return { bg, color };
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '12px' }}>
      {/* Admin Badge */}
      <div style={{
        background: 'linear-gradient(135deg, #c92a2a 0%, #e03131 100%)',
        color: '#ffffff',
        padding: '14px 20px',
        borderRadius: '12px',
        border: '3px solid #1a202c',
        boxShadow: '4px 4px 0 #1a202c',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <SafetyCertificateTwoTone twoToneColor="#ffd43b" style={{ fontSize: '28px' }} />
        <div>
          <Title level={4} style={{ margin: 0, color: '#ffffff', fontWeight: 900 }}>
            Admin Free Form — Kalkulasi BaZi Bebas
          </Title>
          <Text style={{ color: '#ffc9c9', fontWeight: 600, fontSize: '0.85rem' }}>
            Tidak ada batasan tanggal. Gunakan untuk eksplorasi & verifikasi data BaZi kapan saja.
          </Text>
        </div>
      </div>

      {/* Main Form */}
      <div style={{
        border: '3px solid #1a202c',
        borderRadius: '12px',
        padding: '20px',
        backgroundColor: '#ffffff',
        boxShadow: '4px 4px 0 #1a202c',
        marginBottom: '24px'
      }}>
        <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#1a202c' }}>
            <FileTextTwoTone twoToneColor="#1890ff" style={{ marginRight: 8 }} /> Input Data Kelahiran
          </h3>
        </div>

        {errorMsg && (
          <Alert message={errorMsg} type="error" showIcon style={{ marginBottom: '16px', border: '2px solid #e03131', borderRadius: '8px' }} />
        )}

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            gender: 'male',
            timezone: 'Asia/Jakarta',
            tob: dayjs('12:00', 'HH:mm'),
            dob: dayjs()
          }}
          onFinish={handleCalculate}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Nama (opsional)</span>} name="name">
                <Select
                  style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }}
                  placeholder="Pilih atau ketik nama"
                >
                  <Select.Option value="Admin Kalkulasi">Admin Kalkulasi</Select.Option>
                  <Select.Option value="Test User">Test User</Select.Option>
                  <Select.Option value="Demo">Demo</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Jenis Kelamin</span>} name="gender" rules={[{ required: true }]}>
                <Select style={{ height: '40px' }}>
                  <Select.Option value="male"><UserOutlined style={{ marginRight: 4 }} /> Pria (Laki-laki)</Select.Option>
                  <Select.Option value="female"><UserOutlined style={{ marginRight: 4 }} /> Wanita (Perempuan)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Tanggal Lahir</span>} name="dob" rules={[{ required: true, message: 'Pilih tanggal lahir' }]}>
                <DatePicker
                  inputReadOnly
                  needConfirm
                  style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }}
                  // NO disabledDate — admin can pick any date!
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Lahir</span>} name="tob" rules={[{ required: !unknownTob }]}>
                <TimePicker
                  inputReadOnly
                  format="HH:mm"
                  disabled={unknownTob}
                  style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Zona Waktu</span>} name="timezone" rules={[{ required: true }]}>
                <Select style={{ height: '40px' }}>
                  <Select.Option value="Asia/Jakarta">WIB - Asia/Jakarta (GMT+7)</Select.Option>
                  <Select.Option value="Asia/Makassar">WITA - Asia/Makassar (GMT+8)</Select.Option>
                  <Select.Option value="Asia/Jayapura">WIT - Asia/Jayapura (GMT+9)</Select.Option>
                  <Select.Option value="Asia/Singapore">Singapore (GMT+8)</Select.Option>
                  <Select.Option value="Asia/Shanghai">China (GMT+8)</Select.Option>
                  <Select.Option value="Asia/Tokyo">Japan (GMT+9)</Select.Option>
                  <Select.Option value="UTC">UTC (GMT+0)</Select.Option>
                  <Select.Option value="America/New_York">US Eastern (GMT-5)</Select.Option>
                  <Select.Option value="Europe/London">UK (GMT+0)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Lahir Tidak Diketahui?</span>}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '4px' }}>
                  <div
                    onClick={() => setUnknownTob(!unknownTob)}
                    style={{
                      width: '44px',
                      height: '24px',
                      borderRadius: '12px',
                      background: unknownTob ? '#2b8a3e' : '#ced4da',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      border: '2px solid #1a202c',
                    }}
                  >
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      background: '#ffffff',
                      position: 'absolute',
                      top: '2px',
                      left: unknownTob ? '22px' : '2px',
                      transition: 'left 0.2s',
                      border: '1px solid #1a202c',
                    }} />
                  </div>
                  <span style={{ fontWeight: 700, color: '#4a5568', fontSize: '0.85rem' }}>
                    {unknownTob ? 'Ya, jam lahir tidak diketahui' : 'Tidak (jam lahir diketahui)'}
                  </span>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            style={{
              height: '48px',
              background: '#c92a2a',
              borderColor: '#1a202c',
              color: '#ffffff',
              fontWeight: 900,
              fontSize: '1rem',
              boxShadow: '3px 3px 0 #1a202c',
              borderRadius: '10px',
              marginTop: '8px'
            }}
          >
            <ProjectTwoTone style={{ marginRight: 8 }} /> Hitung BaZi
          </Button>
        </Form>
      </div>
          {/* Transit / Custom Date Section */}
          <div style={{
            border: '3px solid #1a202c', borderRadius: '12px', padding: '20px',
            backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c', marginBottom: '20px'
          }}>
            <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#1a202c' }}>
                <ProjectTwoTone style={{ marginRight: 8 }} /> Cek Keselarasan dengan Tanggal Lain
              </h3>
              <div
                onClick={() => setUseTransit(!useTransit)}
                style={{
                  width: '44px', height: '24px', borderRadius: '12px',
                  background: useTransit ? '#2b8a3e' : '#ced4da', position: 'relative', cursor: 'pointer',
                  transition: 'background 0.2s', border: '2px solid #1a202c', flexShrink: 0
                }}
              >
                <div style={{
                  width: '16px', height: '16px', borderRadius: '50%', background: '#ffffff',
                  position: 'absolute', top: '2px', left: useTransit ? '22px' : '2px',
                  transition: 'left 0.2s', border: '1px solid #1a202c'
                }} />
              </div>
            </div>

            {useTransit && (
              <Form
                form={transitForm}
                layout="vertical"
                initialValues={{
                  transit_dob: dayjs(),
                  transit_tob: dayjs('12:00', 'HH:mm'),
                  timezone: 'Asia/Jakarta'
                }}
                onFinish={handleTransitCalculate}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={{ fontWeight: 800 }}>Tanggal Pembanding</span>} name="transit_dob" rules={[{ required: true, message: 'Pilih tanggal pembanding' }]}>
                      <DatePicker
                        inputReadOnly
                        needConfirm
                        style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }}
                        // NO disabledDate — admin can pick ANY date!
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Pembanding</span>} name="transit_tob" rules={[{ required: !transitUnknownTob }]}>
                      <TimePicker
                        inputReadOnly
                        format="HH:mm"
                        disabled={transitUnknownTob}
                        style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={{ fontWeight: 800 }}>Zona Waktu</span>} name="timezone">
                      <Select style={{ height: '40px' }}>
                        <Select.Option value="Asia/Jakarta">WIB - Asia/Jakarta (GMT+7)</Select.Option>
                        <Select.Option value="Asia/Makassar">WITA - Asia/Makassar (GMT+8)</Select.Option>
                        <Select.Option value="Asia/Jayapura">WIT - Asia/Jayapura (GMT+9)</Select.Option>
                        <Select.Option value="UTC">UTC (GMT+0)</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={{ fontWeight: 800 }}>Jam Tidak Diketahui?</span>}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '4px' }}>
                        <div
                          onClick={() => setTransitUnknownTob(!transitUnknownTob)}
                          style={{
                            width: '44px', height: '24px', borderRadius: '12px',
                            background: transitUnknownTob ? '#2b8a3e' : '#ced4da',
                            position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                            border: '2px solid #1a202c'
                          }}
                        >
                          <div style={{
                            width: '16px', height: '16px', borderRadius: '50%', background: '#ffffff',
                            position: 'absolute', top: '2px', left: transitUnknownTob ? '22px' : '2px',
                            transition: 'left 0.2s', border: '1px solid #1a202c'
                          }} />
                        </div>
                        <span style={{ fontWeight: 700, color: '#4a5568', fontSize: '0.85rem' }}>
                          {transitUnknownTob ? 'Ya' : 'Tidak'}
                        </span>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={transitLoading}
                  block
                  style={{
                    height: '42px', background: '#fab005', borderColor: '#1a202c',
                    color: '#1a202c', fontWeight: 900, fontSize: '0.95rem',
                    boxShadow: '3px 3px 0 #1a202c', borderRadius: '10px', marginTop: '6px'
                  }}
                >
                  <ProjectTwoTone style={{ marginRight: 8 }} /> Hitung Keselarasan Tanggal
                </Button>
              </Form>
            )}

            {/* Transit Result */}
            {transitResult && (() => {
              const lbl = (transitResult.bazi_synergy_label || '').toUpperCase();
              let bg = '#ffffe6', color = '#7a7400';
              if (lbl === 'VERY GOOD') { bg = '#e8f4fd'; color = '#0071C1'; }
              if (lbl === 'GOOD') { bg = '#f0f9ec'; color = '#2d7a1e'; }
              if (lbl === 'CHALLENGE') { bg = '#ffe6ff'; color = '#b300b3'; }
              if (lbl === 'BAD') { bg = '#ffe6ee'; color = '#FF0068'; }

              return (
                <div style={{
                  marginTop: '24px', padding: '20px', borderRadius: '12px',
                  border: '3px solid #1a202c', backgroundColor: bg, boxShadow: '3px 3px 0 #1a202c'
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ fontSize: '1rem', color: '#1a202c' }}>Hasil Keselarasan Energi:</strong>
                    <div style={{ fontSize: '2rem', fontWeight: 950, color, marginTop: '6px' }}>
                      {Math.round(Number(transitResult.bazi_synergy_score || 0))}%
                      <span style={{ fontSize: '1.2rem', fontWeight: 800 }}> ({transitResult.bazi_synergy_label})</span>
                    </div>
                  </div>

                  {transitResult.strategy_guide?.narasi && (
                    <div style={{
                      marginTop: '16px', marginBottom: '20px', padding: '16px', borderRadius: '10px',
                      border: '2px solid #1a202c', backgroundColor: '#ffffff', boxShadow: '2px 2px 0 #1a202c',
                      fontSize: '0.85rem', color: '#2d3748', fontWeight: 600, lineHeight: '1.7', whiteSpace: 'pre-line'
                    }}>
                      <div style={{
                        fontWeight: 900, fontSize: '0.8rem', color: '#1a202c', textTransform: 'uppercase',
                        letterSpacing: '0.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px'
                      }}>
                        <span><FileTextTwoTone twoToneColor="#1890ff" style={{ marginRight: 4 }} /> Narasi Fase {transitResult.bazi_synergy_label}</span>
                      </div>
                      {transitResult.strategy_guide.narasi}
                    </div>
                  )}



                  {transitChartData && (
                    <>
                      <Button
                        type="default"
                        onClick={() => setIsTransitChartVisible(true)}
                        style={{
                          fontWeight: 800, background: '#ffffff', color: '#1a202c',
                          borderRadius: '8px', height: '42px', fontSize: '0.9rem',
                          width: '100%', border: '2px solid #1a202c', boxShadow: '2px 2px 0 #1a202c'
                        }}
                      >
                        <EyeTwoTone twoToneColor="#1890ff" style={{ marginRight: 8 }} /> Lihat Bagan Pembanding
                      </Button>

                      <Modal
                        title={<span style={{ fontWeight: 900, fontSize: '1.2rem' }}>Bagan BaZi Pembanding</span>}
                        open={isTransitChartVisible}
                        onCancel={() => setIsTransitChartVisible(false)}
                        footer={null}
                        width={800}
                        centered
                      >
                        <div style={{ overflowX: 'auto', width: '100%', padding: '0 20px' }}>
                          <div style={{ minWidth: '600px' }}>
                            <BaziChartGrid chart={transitChartData} unknownTob={Boolean(transitChartData.unknown_tob)} />
                          </div>
                        </div>
                      </Modal>
                    </>
                  )}

                  {/* DISCLAIMER - Keselarasan Tanggal Custom */}
                  <div style={{
                    marginTop: '16px',
                    padding: '14px 18px',
                    borderRadius: '10px',
                    border: '2px dashed #b08968',
                    backgroundColor: '#fdf6ee',
                    fontSize: '0.78rem',
                    color: '#7a5a2a',
                    fontWeight: 600,
                    lineHeight: '1.7',
                    whiteSpace: 'pre-line',
                    width: '100%',
                    maxWidth: '100%',
                    boxShadow: '1px 1px 0 #b08968'
                  }}>
                    <div style={{
                      fontWeight: 800,
                      fontSize: '0.75rem',
                      color: '#9a7a4a',
                      marginBottom: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}>
                      <SafetyCertificateTwoTone twoToneColor="#b08968" style={{ marginRight: 6 }} /> DISCLAIMER
                    </div>
                    <p style={{ margin: 0 }}>
                      {`Menurut kepercayaan Cina Kuno Baik buruknya seseorang ditentukan oleh 3 Hal :
1. Langit (Sang Pencipta)
2. Bumi (Alam Semesta)
3. Manusia (Diri Sendiri)
Jika seseorang dikatakan buruk bisa jadi terhindar dari nasib buruknya, sebaliknya jika seseorang dikatakan baik bisa saja bernasib buruk.
Semua tergantung dari prinsip keseimbangan dari 3 hal tersebut.`}
                    </p>
                  </div>

                </div>
              );
            })()}
          </div>






      {/* Main Chart Result */}
      {chart && (
        <>
          {/* Score Badges */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '16px' }}>
            {chart.day_master_strength && (
              <span style={{
                backgroundColor: chart.day_master_strength === 'Strong' ? '#ebfbee' : '#fff5f5',
                color: chart.day_master_strength === 'Strong' ? '#2b8a3e' : '#e03131',
                border: '2px solid #1a202c', padding: '6px 12px', borderRadius: '10px',
                fontWeight: 900, boxShadow: '2px 2px 0px 0px #1a202c', fontSize: '0.85rem'
              }}>
                Day Master: {chart.day_master_strength === 'Strong' ? 'DM Strength (Strong)' : 'DM Weak (Weak)'}
              </span>
            )}
            {chart.bazi_compatibility_score !== undefined && (() => {
              const lbl = (chart.bazi_compatibility_label || 'AVERAGE').toUpperCase();
              const s = getScoreBadgeStyle(lbl);
              return (
                <span style={{
                  backgroundColor: s.bg, color: s.color,
                  border: '2px solid #1a202c', padding: '6px 12px', borderRadius: '10px',
                  fontWeight: 900, boxShadow: '2px 2px 0px 0px #1a202c', fontSize: '0.85rem'
                }}>
                  Personal Alignment: {Math.round(Number(chart.bazi_compatibility_score || 0))}% ({chart.bazi_compatibility_label || 'AVERAGE'})
                </span>
              );
            })()}
            {chart.kua && (
              <span style={{
                backgroundColor: '#e8f0fe', color: '#1a73e8',
                border: '2px solid #1a202c', padding: '6px 12px', borderRadius: '10px',
                fontWeight: 900, boxShadow: '2px 2px 0px 0px #1a202c', fontSize: '0.85rem'
              }}>
                Angka Kua: {chart.kua.kua_number} ({chart.kua.trigram} - {chart.kua.group})
              </span>
            )}
          </div>

          {/* BaziChartGrid Button */}
          <Button
            type="primary"
            onClick={() => setIsChartVisible(true)}
            style={{
              fontWeight: 900, background: '#1a202c', color: '#fff',
              borderRadius: '8px', height: '46px', fontSize: '1rem',
              width: '100%', maxWidth: '100%',
              border: '3px solid #1a202c',
              boxShadow: '3px 3px 0 #1a202c',
              marginBottom: '20px'
            }}
          >
            <ProjectTwoTone style={{ marginRight: 8 }} /> Lihat Bagan Lahir
          </Button>

          <Modal
            title={<span style={{ fontWeight: 900, fontSize: '1.2rem' }}>Bagan Lahir (Birth Chart)</span>}
            open={isChartVisible}
            onCancel={() => setIsChartVisible(false)}
            footer={null}
            width={800}
            centered
          >
            <div style={{ overflowX: 'auto', width: '100%', padding: '0 20px' }}>
              <div style={{ minWidth: '600px' }}>
                <BaziChartGrid chart={chart} unknownTob={Boolean(chart.unknown_tob)} />
              </div>
            </div>
          </Modal>

          {/* Self-Clash warnings */}
          {chart.internal_clashes && chart.internal_clashes.length > 0 && (
            <div style={{
              width: '100%', border: '2px solid #fa5252', borderRadius: '10px',
              padding: '12px 16px', backgroundColor: '#fff5f5',
              boxShadow: '3px 3px 0 #1a202c', marginBottom: '20px'
            }}>
              <strong style={{ color: '#fa5252', display: 'block', marginBottom: '8px', fontSize: '0.88rem' }}>
                <WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 8 }} /> Peringatan Bentrokan Internal (Self-Clash):
              </strong>
              {chart.internal_clashes.map((clash, cIdx) => (
                <div key={cIdx} style={{
                  marginBottom: cIdx < (chart.internal_clashes?.length || 0) - 1 ? '8px' : '0',
                  borderBottom: cIdx < (chart.internal_clashes?.length || 0) - 1 ? '1px dashed #ffa8a8' : 'none',
                  paddingBottom: cIdx < (chart.internal_clashes?.length || 0) - 1 ? '6px' : '0'
                }}>
                  <strong style={{ fontSize: '0.84rem', color: '#c92a2a' }}>{clash.name}</strong>
                  <p style={{ fontSize: '0.78rem', color: '#495057', margin: '2px 0 0 0', fontWeight: 600 }}>{clash.impact}</p>
                </div>
              ))}
            </div>
          )}

          {/* Strategy Guide */}
          {chart.strategy_guide && (
            <div style={{
              width: '100%', border: '3px solid #1a202c', borderRadius: '12px',
              padding: '20px', backgroundColor: '#ffffff',
              boxShadow: '4px 4px 0 #1a202c', marginBottom: '20px'
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#1a202c' }}>
                  <BulbTwoTone twoToneColor="#fadb14" style={{ marginRight: 8 }} /> Panduan Strategi Buat Kamu
                </h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                {[
                  { title: <><MoneyCollectTwoTone twoToneColor="#f59f00" style={{ marginRight: 4 }} /> Keuangan</>, text: chart.strategy_guide.keuangan },
                  { title: <><HeartTwoTone twoToneColor="#e03131" style={{ marginRight: 4 }} /> Kesehatan</>, text: chart.strategy_guide.kesehatan },
                  { title: <><TeamOutlined style={{ marginRight: 4, color: '#1098ad' }} /> Hubungan Keluarga</>, text: chart.strategy_guide.keluarga },
                  { title: <><TeamOutlined style={{ marginRight: 4, color: '#2b8a3e' }} /> Partner Bisnis</>, text: chart.strategy_guide.partner },
                  { title: <><BankTwoTone twoToneColor="#1971c2" style={{ marginRight: 4 }} /> Pekerjaan</>, text: chart.strategy_guide.pekerjaan },
                  { title: <><BankTwoTone twoToneColor="#d9480f" style={{ marginRight: 4 }} /> Bisnis</>, text: chart.strategy_guide.bisnis }
                ].map((item, idx) => (
                  <div key={idx} style={{
                    border: '2px solid #1a202c', borderRadius: '10px',
                    padding: '12px 14px', backgroundColor: '#ffffff',
                    boxShadow: '2px 2px 0 #1a202c'
                  }}>
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1a202c', marginBottom: '4px' }}>{item.title}</div>
                    <p style={{ fontSize: '0.78rem', color: '#4a5568', margin: 0, lineHeight: '1.5', fontWeight: 600 }}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Karakteristik & Gaya Kinerja */}
          {chart.career_health?.info && (
            <div style={{
              width: '100%', border: '3px solid #1a202c', borderRadius: '12px',
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c',
              marginBottom: '20px'
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#1a202c' }}>
                  <StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 8 }} /> Karakteristik & Gaya Kinerja Bawaan
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {chart.career_health.info.karakteristik && (
                  <div style={{ border: '2px solid #1a202c', borderRadius: '10px', padding: '12px 14px', backgroundColor: '#f8f9fa', boxShadow: '2px 2px 0 #1a202c' }}>
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#1098ad', marginBottom: '4px' }}><PushpinTwoTone twoToneColor="#1098ad" style={{ marginRight: 4 }} /> Karakteristik Utama</div>
                    <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 600, whiteSpace: 'pre-line' }}>{chart.career_health.info.karakteristik}</p>
                  </div>
                )}
                {chart.career_health.info.kinerja && (
                  <div style={{ border: '2px solid #1a202c', borderRadius: '10px', padding: '12px 14px', backgroundColor: '#ebfbee', boxShadow: '2px 2px 0 #1a202c' }}>
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#2b8a3e', marginBottom: '4px' }}><ThunderboltTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Gaya Kinerja & Lingkungan Kerja Ideal</div>
                    <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 600, whiteSpace: 'pre-line' }}>{chart.career_health.info.kinerja}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Peluang Bisnis & Pekerjaan */}
          {chart.career_health?.details?.karir && chart.career_health.details.karir.length > 0 && (
            <div style={{
              width: '100%', border: '3px solid #1a202c', borderRadius: '12px',
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c',
              marginBottom: '20px'
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#1a202c' }}>
                  <BankTwoTone twoToneColor="#fa8c16" style={{ marginRight: 8 }} /> Peluang Bisnis & Pekerjaan Ideal
                </h3>
              </div>
              {chart.career_health.details.karir.map((item, idx) => (
                <div key={idx} style={{ border: '2px solid #1a202c', borderRadius: '10px', padding: '14px', backgroundColor: '#fff9db', boxShadow: '2px 2px 0 #1a202c', marginBottom: '12px' }}>
                  <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 650, whiteSpace: 'pre-line' }}>{item}</p>
                </div>
              ))}
            </div>
          )}

          {/* Kua Directions */}
          {chart.kua?.directions && (
            <div style={{
              width: '100%', border: '3px solid #1a202c', borderRadius: '12px',
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c',
              marginBottom: '20px'
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#1a202c' }}>
                  <CompassTwoTone twoToneColor="#1890ff" style={{ marginRight: 8 }} /> Sektor Arah Keberuntungan (Kua {chart.kua.kua_number} - {chart.kua.trigram})
                </h3>
              </div>
              {chart.kua.directions.favorable && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#2b8a3e', marginBottom: '8px' }}>                  <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Arah Keberuntungan:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                    {Object.entries(chart.kua.directions.favorable).map(([key, dir]) => (
                      <div key={key} style={{ border: '2px solid #2b8a3e', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#ebfbee' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#2b8a3e' }}>{dir.name} ({dir.direction})</div>
                        <div style={{ fontSize: '0.75rem', color: '#495057', fontWeight: 600 }}>{dir.meaning}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {chart.kua.directions.unfavorable && (
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#e03131', marginBottom: '8px' }}>                  <CloseCircleTwoTone twoToneColor="#f5222d" style={{ marginRight: 4 }} /> Arah Perlu Diwaspadai:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                    {Object.entries(chart.kua.directions.unfavorable).map(([key, dir]) => (
                      <div key={key} style={{ border: '2px solid #e03131', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#fff5f5' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#e03131' }}>{dir.name} ({dir.direction})</div>
                        <div style={{ fontSize: '0.75rem', color: '#495057', fontWeight: 600 }}>{dir.meaning}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Da Yun / Luck Cycles */}
          {chart.luck_cycles && chart.luck_cycles.length > 0 && (() => {
            const currentYear = new Date().getFullYear();
            const elChToEn: Record<string, string> = { '木': 'wood', '火': 'fire', '土': 'earth', '金': 'metal', '水': 'water' };
            const dayPillar = chart.pillars?.find(p => p.position === 'day');
            const dmElement = dayPillar?.element ? elChToEn[dayPillar.element] : null;
            const generates: Record<string, string> = { wood: 'fire', fire: 'earth', earth: 'metal', metal: 'water', water: 'wood' };
            const ctrls: Record<string, string> = { wood: 'earth', earth: 'water', water: 'fire', fire: 'metal', metal: 'wood' };
            const getRelation = (ce: string, dm: string): 'generates' | 'same' | 'draining' | 'controls' | 'resisted' => {
              if (ce === dm) return 'same';
              if (generates[ce] === dm) return 'generates';
              if (generates[dm] === ce) return 'draining';
              if (ctrls[ce] === dm) return 'controls';
              return 'resisted';
            };
            const getTrend = (ce: string, dm: string, strong: boolean, elements: any): { dir: string; label: string; color: string } => {
              const rel = getRelation(ce, dm);
              let isFav = strong
                ? (rel === 'controls' || rel === 'draining')
                : (rel === 'generates' || rel === 'same');

              // Advanced rule (Shi Shen Zhi Sha): 
              // If DM is Weak, but Power (controls DM) is excessive (> 2), Output (draining) becomes Favorable to counter Power.
              if (!strong && rel === 'draining') {
                const powerElement = Object.keys(ctrls).find(k => ctrls[k] === dm);
                if (powerElement && elements && elements[powerElement] > 2) {
                  isFav = true;
                }
              }

              if (isFav) return { dir: '▲', label: 'Naik', color: '#2b8a3e' };
              if (rel === 'controls' || rel === 'resisted') return { dir: '◆', label: 'Normal', color: '#f59f00' };
              return { dir: '▼', label: 'Turun', color: '#e03131' };
            };
            return (
              <div style={{
                width: '100%', border: '3px solid #1a202c', borderRadius: '12px',
                padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c',
                marginBottom: '20px'
              }}>
                <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#1a202c' }}>
                    <FieldTimeOutlined style={{ marginRight: 8, color: '#1a202c' }} /> Pilar Keberuntungan 10 Tahun (Da Yun)
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#4a5568', fontWeight: 600 }}>
                    Siklus perjalanan energi hidup setiap 10 tahun sekali.
                  </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                  {chart.luck_cycles.map((cycle, idx) => {
                    const isActive = currentYear >= cycle.start_year && currentYear <= cycle.end_year;
                    const elColorMap: Record<string, string> = { wood: '#2b8a3e', fire: '#e03131', earth: '#d9480f', metal: '#495057', water: '#1971c2' };
                    const badgeColor = elColorMap[cycle.element.toLowerCase()] || '#1a202c';
                    const trend = dmElement ? getTrend(cycle.element.toLowerCase(), dmElement, chart.day_master_strength === 'Strong', chart.elements) : null;
                    return (
                      <div key={idx} style={{
                        border: isActive ? '3px solid #2b8a3e' : '2px solid #1a202c',
                        borderRadius: '10px', padding: '12px',
                        backgroundColor: isActive ? '#ebfbee' : '#f8f9fa',
                        boxShadow: isActive ? '3px 3px 0 #2b8a3e' : '2px 2px 0 #1a202c',
                        position: 'relative', textAlign: 'center'
                      }}>
                        {isActive && (
                          <span style={{
                            position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                            backgroundColor: '#2b8a3e', color: '#ffffff', fontSize: '0.65rem', fontWeight: 900,
                            padding: '2px 8px', borderRadius: '12px', whiteSpace: 'nowrap', border: '1px solid #1a202c'
                          }}>
                            <FireTwoTone twoToneColor="#fa541c" style={{ marginRight: 8 }} /> PERIODE AKTIF
                          </span>
                        )}
                        {trend && (
                          <div style={{
                            position: 'absolute', top: 0, left: 0, width: '6px', height: '100%',
                            backgroundColor: trend.color, borderRadius: '8px 0 0 8px'
                          }} />
                        )}
                        <div style={{ fontWeight: 900, fontSize: '0.85rem', color: '#1a202c', marginTop: isActive ? '4px' : 0 }}>
                          Usia {cycle.start_age}-{cycle.end_age} Thn
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 700, marginBottom: '6px' }}>
                          ({cycle.start_year}-{cycle.end_year})
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 950, color: badgeColor }}>
                          {cycle.stem}{cycle.branch}
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#4a5568', marginTop: '2px' }}>
                          {cycle.pinyin}
                        </div>
                        {trend && (
                          <div style={{
                            marginTop: '8px', padding: '4px 12px', borderRadius: '8px',
                            backgroundColor: trend.color + '1a',
                            border: '2px solid ' + trend.color,
                            color: trend.color, fontWeight: 900, fontSize: '0.75rem',
                            display: 'inline-block', letterSpacing: '0.5px'
                          }}>
                            {trend.dir} {trend.label}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#868e96', borderTop: '1px solid #dee2e6', paddingTop: '10px' }}>
                  <span style={{ color: '#2b8a3e' }}>▲ Naik</span>
                  <span style={{ color: '#f59f00' }}>◆ Normal</span>
                  <span style={{ color: '#e03131' }}>▼ Turun</span>
                  <span style={{ color: '#868e96' }}>— relatif terhadap Day Master ({chart.day_master_strength === 'Strong' ? 'Kuat' : 'Lemah'})</span>
                </div>
              </div>
            );
          })()}

          {/* Ten Gods Profile */}
          {chart.ten_relations && (() => {
            const relMap: Record<string, string> = {
              '比肩': 'F', '劫财': 'RW', '食神': 'EG', '伤官': 'HO', '偏财': 'IW',
              '正财': 'DW', '七杀': '7K', '正官': 'DO', '偏印': 'IR', '正印': 'DR'
            };
            const monthRel = chart.ten_relations.month_stem || '';
            const yearRel = chart.ten_relations.year_stem || '';
            const hourRel = chart.ten_relations.hour_stem || '';
            const dominantCode = relMap[monthRel] || relMap[yearRel] || relMap[hourRel] || 'DW';
            const profiles = tenGodsProfiles as Record<string, any>;
            const dominantProfile = profiles[dominantCode];

            return (
              <div style={{
                width: '100%', border: '3px solid #1a202c', borderRadius: '12px',
                padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c',
                marginBottom: '20px'
              }}>
                <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#1a202c' }}>
                    <ProjectTwoTone style={{ marginRight: 8 }} /> Profil 10 Dewa (Ten Gods) & Bintang Dominan
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#4a5568', fontWeight: 600 }}>
                    Karakter psikologis, bakat tersembunyi, dan kecocokan karir bawaan lahir.
                  </p>
                </div>
                {dominantProfile && (
                  <div style={{
                    border: '3px solid #fab005', borderRadius: '12px', padding: '16px',
                    backgroundColor: '#fff9db', boxShadow: '3px 3px 0 #1a202c', marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                      <span style={{
                        backgroundColor: '#fab005', color: '#1a202c', fontWeight: 950, fontSize: '0.75rem',
                        padding: '3px 10px', borderRadius: '20px', border: '1.5px solid #1a202c'
                      }}>
                        <StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 8 }} /> BINTANG PALING DOMINAN
                      </span>
                      <span style={{ fontWeight: 900, fontSize: '0.9rem', color: '#d9480f' }}>
                        {dominantProfile.name} ({dominantProfile.code})
                      </span>
                    </div>
                    <h4 style={{ margin: '4px 0 10px 0', fontSize: '1.2rem', fontWeight: 950, color: '#1a202c' }}>
                      {dominantProfile.title}
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[
                        { title: <><PushpinTwoTone twoToneColor="#1098ad" style={{ marginRight: 4 }} /> Karakteristik Utama</>, text: dominantProfile.karakteristik, color: '#1098ad' },
                        { title: <><ThunderboltTwoTone twoToneColor="#52c41a" style={{ marginRight: 4 }} /> Potensi Kekuatan</>, text: dominantProfile.potensi, color: '#2b8a3e', icon: <RocketTwoTone twoToneColor="#52c41a" style={{ marginRight: 8 }} /> },
                        { title: <><BankTwoTone twoToneColor="#1971c2" style={{ marginRight: 4 }} /> Kecocokan Pekerjaan</>, text: dominantProfile.pekerjaan, color: '#1971c2' },
                        { title: <><BankTwoTone twoToneColor="#d9480f" style={{ marginRight: 4 }} /> Kecocokan Usaha / Bisnis</>, text: dominantProfile.usaha, color: '#d9480f' },
                      ].map((item, idx) => (
                        <div key={idx} style={{ border: '1.5px solid #1a202c', borderRadius: '8px', padding: '10px', backgroundColor: '#ffffff' }}>
                          <div style={{ fontWeight: 800, fontSize: '0.82rem', color: item.color, marginBottom: '2px' }}>{item.icon}{item.title}</div>
                          <div style={{ fontSize: '0.78rem', color: '#2d3748', fontWeight: 600, lineHeight: '1.5' }}>{item.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}


          {/* Potensi Penyakit & Kesehatan */}
          {chart.career_health?.details?.penyakit && chart.career_health.details.penyakit.length > 0 && (
            <div style={{ 
              width: '100%', maxWidth: '100%', border: '3px solid #1a202c', borderRadius: '12px', 
              padding: '20px', backgroundColor: '#ffffff', boxShadow: '4px 4px 0 #1a202c' 
            }}>
              <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '10px', marginBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px', color: '#e03131' }}>
                  <HeartTwoTone twoToneColor="#e03131" style={{ marginRight: 8 }} /> Potensi Penyakit & Panduan Kesehatan
                </h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {chart.career_health.details.penyakit.map((item, idx) => (
                  <div key={idx} style={{ border: '2px solid #e03131', borderRadius: '10px', padding: '14px', backgroundColor: '#fff5f5', boxShadow: '2px 2px 0 #1a202c' }}>
                    <p style={{ fontSize: '0.8rem', color: '#2d3748', margin: 0, lineHeight: '1.6', fontWeight: 650, whiteSpace: 'pre-line' }}>{item}</p>
                  </div>
                ))}
              </div>

            </div>
          )
        }

          {/* ⬇️ NARASI */}
          <div style={{
            marginTop: '8px',
            padding: '14px 18px',
            borderRadius: '10px',
            border: '2px dashed #b08968',
            backgroundColor: '#fdf6ee',
            fontSize: '0.78rem',
            color: '#7a5a2a',
            fontWeight: 600,
            lineHeight: '1.7',
            whiteSpace: 'pre-line',
            width: '100%',
            maxWidth: '100%',
            boxShadow: '1px 1px 0 #b08968'
          }}>
            <div style={{
              fontWeight: 800,
              fontSize: '0.75rem',
              color: '#9a7a4a',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.3px'
            }}>
              <SafetyCertificateTwoTone twoToneColor="#b08968" style={{ marginRight: 6 }} /> DISCLAIMER
            </div>
            <p style={{ margin: 0 }}>
              {`Menurut kepercayaan Cina Kuno Baik buruknya seseorang ditentukan oleh 3 Hal :
1. Langit (Sang Pencipta)
2. Bumi (Alam Semesta)
3. Manusia (Diri Sendiri)
Jika seseorang dikatakan buruk bisa jadi terhindar dari nasib buruknya, sebaliknya jika seseorang dikatakan baik bisa saja bernasib buruk.
Semua tergantung dari prinsip keseimbangan dari 3 hal tersebut.`}
            </p>
          </div>

        </>
      )}
    </div>
  );
};

export default AdminFreeForm;
